"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import Layout from "../common/Layout";
import { useNotification } from "../../context/NotificationContext";
import apiClient from "../../context/apiClient";

import {
  Container,
  Paper,
  Box,
  Typography,
  Grid,
  Chip,
  Avatar,
  Divider,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Skeleton,
  Alert,
  AlertTitle,
  CircularProgress,
  CssBaseline,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Pagination,
  Fade,
  Zoom,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

// Icon imports
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PsychologyIcon from "@mui/icons-material/Psychology";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import BugReportIcon from "@mui/icons-material/BugReport";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import QuizIcon from "@mui/icons-material/Quiz";
import GppBadIcon from "@mui/icons-material/GppBad";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CodeIcon from "@mui/icons-material/Code";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import InfoIcon from "@mui/icons-material/Info";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import InterestsIcon from "@mui/icons-material/Interests";
// --- NEW ICONS FOR ENHANCEMENTS ---
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";



const getStatusChipProps = (status) => {
  switch (status) {
    case "COMPLETED":
      return { color: "success", label: "Completed" };
    case "IN_PROGRESS":
      return { color: "warning", label: "In Progress" };
    case "TERMINATED_MALPRACTICE":
      return { color: "error", label: "Terminated" };
    default:
      return {
        color: "default",
        label: status?.replace(/_/g, " ") || "Unknown",
      };
  }
};

const formatDateTime = (isoString, options = {}) => {
  if (!isoString) return "N/A";
  const defaultOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Date(isoString).toLocaleString("en-US", {
    ...defaultOptions,
    ...options,
  });
};

const SectionPaper = ({ title, icon, children, sx }) => (
  <Paper
    elevation={0}
    sx={{
      bgcolor: theme => alpha(theme.palette.primary.main, 0.03),
      p: 2,
      borderRadius: "24px",
      border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      height: "100%",
      position: "relative",
      overflow: "hidden",
      ...sx,
    }}
  >
    <Paper
      elevation={0}
      sx={{
        bgcolor: "background.paper",
        p: 3,
        borderRadius: "20px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: theme => `0 8px 32px ${alpha(theme.palette.common.black, 0.04)}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        {icon &&
          React.isValidElement(icon) &&
          React.cloneElement(icon, { sx: { color: "primary.main", fontSize: 28 } })}
        <Typography variant="h6" color="text.primary" fontWeight={700}>
          {title}
        </Typography>
      </Stack>
      <Box sx={{ flexGrow: 1 }}>{children}</Box>
    </Paper>
  </Paper>
);


const OverallScoreProgress = ({ value, isCompleted }) => {
  const theme = useTheme();
  const scoreColor = isCompleted ? theme.palette.primary.main : theme.palette.text.disabled;
  
  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={180}
        thickness={3}
        sx={{ color: alpha(theme.palette.primary.main, 0.1) }}
      />
      <CircularProgress
        variant="determinate"
        value={isCompleted ? value : 0}
        size={180}
        thickness={3}
        sx={{
          color: scoreColor,
          position: "absolute",
          left: 0,
          filter: `drop-shadow(0 0 8px ${alpha(theme.palette.primary.main, 0.4)})`,
          "& .MuiCircularProgress-circle": {
            strokeLinecap: "round",
            transition: "stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)",
          },
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {isCompleted ? (
          <>
            <Typography
              variant="h3"
              component="div"
              fontWeight="900"
              sx={{ color: "primary.main", letterSpacing: -1 }}
            >
              {`${Math.round(value)}%`}
            </Typography>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, mt: -1 }}>
              Overall Score
            </Typography>
          </>
        ) : (
          <Typography
            variant="h5"
            component="div"
            fontWeight="bold"
            color="text.disabled"
          >
            Pending
          </Typography>
        )}
      </Box>
    </Box>
  );
};


const ReportSummaryCard = React.memo(({ report, onSelect, index }) => {
  const theme = useTheme();
  const statusProps = getStatusChipProps(report.status);
  const scoreColor = report.status === "COMPLETED" ? "primary.main" : "text.disabled";
  const scoreLabel = report.status === "COMPLETED" ? `${report.global_readiness_score}%` : "Pending";

  return (
    <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
      <Card
        sx={{
          bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
          p: 1,
          borderRadius: "24px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          cursor: "pointer",
          border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: theme => `0 20px 40px ${alpha(theme.palette.common.black, 0.1)}`,
            borderColor: "primary.main",
            "& .view-report-btn": {
              bgcolor: "primary.main",
              color: "common.white",
            }
          }
        }}
        onClick={() => onSelect(report.id)}
      >
        <Paper
          elevation={0}
          sx={{
            flexGrow: 1,
            p: 3,
            bgcolor: "background.paper",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            mb={3}
            flexShrink={0}
          >
            <Avatar 
              sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                color: "primary.main",
                width: 60, 
                height: 60,
                fontWeight: 800,
                fontSize: "1.2rem",
                border: theme => `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              {report.user?.charAt(0).toUpperCase() || "U"}
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{ color: "text.primary", fontWeight: 700, lineHeight: 1.2 }}
              >
                {report.user}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {report.position_applied}
              </Typography>
            </Box>
          </Stack>
          
          <Divider sx={{ mb: 2.5, opacity: 0.5 }} />
          
          <Stack spacing={2} flexGrow={1} justifyContent="center">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
                Interview Date
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                {formatDateTime(report.interview_start_time, {
                  hour: undefined,
                  minute: undefined,
                })}
              </Typography>
            </Stack>
            
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
                Status
              </Typography>
              <Chip
                label={statusProps.label}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette[statusProps.color].main, 0.1),
                  color: theme.palette[statusProps.color].main,
                  fontWeight: 800,
                  fontSize: "0.7rem",
                  borderRadius: "8px",
                  border: `1px solid ${alpha(theme.palette[statusProps.color].main, 0.2)}`
                }}
              />
            </Stack>
            
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
                Readiness Score
              </Typography>
              <Typography
                variant="h6"
                sx={{ 
                  fontWeight: 900, 
                  color: scoreColor,
                  fontSize: "1.1rem"
                }}
              >
                {scoreLabel}
              </Typography>
            </Stack>
          </Stack>
        </Paper>
        <Box sx={{ p: 1.5 }}>
          <Button
            fullWidth
            className="view-report-btn"
            variant="text"
            sx={{ 
              borderRadius: "15px", 
              py: 1.2,
              fontWeight: 700,
              transition: "all 0.2s"
            }}
          >
            View Full Report
          </Button>
        </Box>
      </Card>
    </Zoom>
  );
});


const ReportsList = ({
  reports,
  onSelect,
  loading,
  onRefresh,
  pagination,
  onPageChange,
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const filteredReports = useMemo(
    () =>
      reports.filter((report) => {
        const matchesSearch =
          report.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.position_applied
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        const matchesStatus =
          statusFilter === "ALL" || report.status === statusFilter;
        return matchesSearch && matchesStatus;
      }),
    [reports, searchTerm, statusFilter]
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 6,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            component="h1"
            sx={{ 
              color: "text.primary", 
              fontWeight: 800,
              letterSpacing: -1.5,
              mb: 1
            }}
          >
            My Reports
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, opacity: 0.8 }}>
            {loading ? (
              <Skeleton width="300px" />
            ) : (
              `Access your intelligent interview performance analysis.`
            )}
          </Typography>
        </Box>
        <IconButton
          onClick={onRefresh}
          disabled={loading}
          sx={{ 
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
            p: 1.5,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            "&:hover": {
              bgcolor: "primary.main",
              color: "common.white",
            }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>


      {loading ? (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
          >
            <Skeleton
              variant="rectangular"
              width="100%"
              height={56}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width={200}
              height={56}
              sx={{ borderRadius: 1, flexShrink: 0 }}
            />
          </Stack>
        </Paper>
      ) : (
        <Paper 
          elevation={0}
          sx={{ 
            p: 3, 
            mb: 5, 
            borderRadius: "24px",
            bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
            border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            alignItems="center"
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name or position..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "primary.main" }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: "16px",
                  bgcolor: "background.paper",
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.divider, 0.1),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: "primary.main",
                  }
                }
              }}
            />
            <TextField
              select
              label="Interview Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ 
                minWidth: 240,
                '& .MuiOutlinedInput-root': {
                  borderRadius: "16px",
                  bgcolor: "background.paper",
                }
              }}
            >
              <option value="ALL">All Reports</option>
              <option value="COMPLETED">Completed</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="TERMINATED_MALPRACTICE">Terminated</option>
            </TextField>
          </Stack>
        </Paper>

      )}

      <Grid container spacing={4} justifyContent="center">
        {loading ? (
          Array.from(new Array(9)).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Skeleton
                variant="rectangular"
                height={380}
                sx={{ borderRadius: "16px" }}
              />
            </Grid>
          ))
        ) : filteredReports.length > 0 ? (
          filteredReports.map((report, index) => (
            <Grid item xs={12} sm={6} md={4} key={report.id}>
              <ReportSummaryCard
                report={report}
                onSelect={onSelect}
                index={index}
              />
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: "center", mt: 4 }}>
              <SearchIcon
                sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.primary">
                No reports found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search criteria or check another page.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
      {pagination.totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={onPageChange}
            color="primary"
            size="large"
            disabled={loading}
          />
        </Box>
      )}
    </Container>
  );
};

const ScoreProgressBar = React.memo(({ label, score, isCompleted }) => {
  const theme = useTheme();
  return (
    <Box sx={{ width: "100%", my: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.primary" fontWeight={600}>
          {label}
        </Typography>
        {isCompleted ? (
          <Typography
            variant="h6"
            sx={{ color: "primary.main", fontSize: "0.95rem" }}
            fontWeight="bold"
          >{`${score}%`}</Typography>
        ) : (
          <Chip label="Pending" size="small" variant="outlined" sx={{ borderRadius: "6px", fontSize: "0.7rem" }} />
        )}
      </Box>
      <LinearProgress
        variant={isCompleted ? "determinate" : "indeterminate"}
        value={isCompleted ? score : 0}
        sx={{ 
          height: 10, 
          borderRadius: 5,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          "& .MuiLinearProgress-bar": {
            borderRadius: 5,
            bgcolor: "primary.main",
          }
        }}
      />
    </Box>
  );
});


const TerminatedReportDetail = ({ reportData, onBack }) => {
  const {
    user,
    position_applied,
    interview_start_time,
    interview_end_time,
    malpractice_reason,
  } = reportData;
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Fade in={true} timeout={600}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <IconButton onClick={onBack} sx={{ mr: 1, color: "primary.main" }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" component="div">
              Terminated Interview Report
            </Typography>
          </Box>
          <SectionPaper title="Interview Terminated">
            <Stack alignItems="center" spacing={2} textAlign="center" mb={3}>
              <Avatar sx={{ bgcolor: "error.main", width: 80, height: 80 }}>
                <ReportProblemIcon sx={{ fontSize: "2.5rem" }} />
              </Avatar>
              <Box>
                <Typography variant="h5" component="h1">
                  {user}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {position_applied}
                </Typography>
              </Box>
            </Stack>
            <Alert severity="error" icon={<GppBadIcon />} sx={{ mb: 3 }}>
              <AlertTitle sx={{ fontWeight: "bold" }}>
                Reason for Termination
              </AlertTitle>
              {malpractice_reason ||
                "A malpractice event was detected, and the interview was concluded prematurely."}
            </Alert>
            <Divider sx={{ my: 3 }} />
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CalendarTodayIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Interview Started"
                  secondary={formatDateTime(interview_start_time)}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <TimerOffIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary="Interview Terminated"
                  secondary={formatDateTime(interview_end_time)}
                />
              </ListItem>
            </List>
          </SectionPaper>
        </Box>
      </Fade>
    </Container>
  );
};

const InterviewReportDetailSkeleton = ({ onBack }) => (
  <Container maxWidth="xl" sx={{ py: 4 }}>
    <Fade in={true} timeout={300}>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <IconButton onClick={onBack} sx={{ mr: 1, color: "primary.main" }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="div">
            <Skeleton width="250px" />
          </Typography>
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <SectionPaper
              title={<Skeleton width="60%" />}
              icon={<Skeleton variant="circular" width={24} height={24} />}
            >
              <Stack alignItems="center" spacing={2} textAlign="center">
                <Skeleton variant="circular" width={80} height={80} />
                <Box>
                  <Typography variant="h5" component="h1">
                    <Skeleton width={150} />
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    <Skeleton width={200} />
                  </Typography>
                </Box>
                <Skeleton variant="rounded" width={120} height={40} />
              </Stack>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ px: 1, textAlign: "left" }}>
                <Skeleton width="80%" sx={{ mb: 1.5 }} />
                <Skeleton width="60%" sx={{ mb: 2.5, ml: 4 }} />
                <Skeleton width="90%" sx={{ mb: 1.5 }} />
                <Stack direction="row" spacing={1} sx={{ pl: 4 }}>
                  <Skeleton variant="rounded" width={80} height={24} />
                  <Skeleton variant="rounded" width={100} height={24} />
                </Stack>
              </Box>
              <Divider sx={{ my: 3 }} />
              <List dense>
                {[1, 2, 3].map((i) => (
                  <ListItem key={i}>
                    <ListItemIcon>
                      <Skeleton variant="circular" width={24} height={24} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Skeleton width="40%" />}
                      secondary={<Skeleton width="60%" />}
                    />
                  </ListItem>
                ))}
              </List>
            </SectionPaper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack spacing={4}>
              <SectionPaper
                title={<Skeleton width="40%" />}
                icon={<Skeleton variant="circular" width={24} height={24} />}
              >
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} sm={5} textAlign="center">
                    <Skeleton variant="circular" width={160} height={160} />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    {[1, 2, 3].map((i) => (
                      <Box sx={{ width: "100%", my: 1.5 }} key={i}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <Skeleton width="50%" />
                          <Skeleton width="20%" />
                        </Box>
                        <Skeleton
                          variant="rectangular"
                          height={8}
                          sx={{ borderRadius: 5 }}
                        />
                      </Box>
                    ))}
                  </Grid>
                </Grid>
              </SectionPaper>
              <SectionPaper
                title={<Skeleton width="50%" />}
                icon={<Skeleton variant="circular" width={24} height={24} />}
              >
                <Grid container spacing={2}>
                  {[1, 2, 3, 4].map((i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Card variant="outlined" sx={{ height: "100%" }}>
                        <CardHeader
                          avatar={
                            <Skeleton
                              variant="circular"
                              width={40}
                              height={40}
                            />
                          }
                          title={<Skeleton height={20} width="80%" />}
                          action={
                            <Skeleton
                              variant="rounded"
                              width={50}
                              height={24}
                            />
                          }
                        />
                        <CardContent sx={{ pt: 1 }}>
                          <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                          <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                          <Skeleton
                            variant="text"
                            width="70%"
                            sx={{ fontSize: "1rem" }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </SectionPaper>
              <SectionPaper
                title={<Skeleton width="45%" />}
                icon={<Skeleton variant="circular" width={24} height={24} />}
              >
                <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                <Skeleton
                  variant="text"
                  width="80%"
                  sx={{ fontSize: "1rem" }}
                />
              </SectionPaper>
              <SectionPaper
                title={<Skeleton width="55%" />}
                icon={<Skeleton variant="circular" width={24} height={24} />}
              >
                <Skeleton
                  variant="rectangular"
                  height={56}
                  sx={{ borderRadius: 1 }}
                />
              </SectionPaper>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  </Container>
);

const InterviewReportDetail = ({ reportData, onBack }) => {
  const theme = useTheme();
  const roundMetadata = useMemo(() => ({
    write_program: { title: "Programming", icon: <CodeIcon /> },
    predict_output: { title: "Predict Output", icon: <QuizIcon /> },
    fix_error: { title: "Debugging", icon: <BugReportIcon /> },
    psychometric: { title: "Psychometric", icon: <PsychologyIcon /> },
    communication: { title: "Communication", icon: <RecordVoiceOverIcon /> },
  }), []);

  const roundDetails = useMemo(() => {
    const roundAnalysis = reportData?.round_analysis_json;
    if (!roundAnalysis) return [];
    const details = [];
    Object.entries(roundAnalysis).forEach(([key, value]) => {
      if (key === "technical" && typeof value === "object" && value !== null) {
        Object.entries(value).forEach(([subKey, subValue]) => {
          if (subValue && subValue.overall_score !== undefined) {
            details.push({
              key: `technical-${subKey}`,
              title: subKey
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()),
              icon: <MilitaryTechIcon />,
              data: subValue,
            });
          }
        });
      } else if (value && value.overall_score !== undefined) {
        details.push({
          key,
          title:
            roundMetadata[key]?.title ||
            key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
          icon: roundMetadata[key]?.icon || <HelpOutlineIcon />,
          data: value,
        });
      }
    });
    return details;
  }, [reportData, roundMetadata]);

  if (!reportData) return <InterviewReportDetailSkeleton onBack={onBack} />;

  if (reportData.status === "TERMINATED_MALPRACTICE")
    return <TerminatedReportDetail reportData={reportData} onBack={onBack} />;

  const {
    status,
    user,
    position_applied,
    interview_start_time,
    identity_verified,
    malpractice_detected,
    global_readiness_score,
    language_proficiency_score,
    communication_overall_score,
    psychometric_overall_score,
    technical_specialization_scores,
    language_analysis,
    candidate_experience,
    aiml_specialization,
    full_qa_transcript,
  } = reportData;
  const isCompleted = status === "COMPLETED";
  const statusChipProps = getStatusChipProps(status);

  const formatTechnicalLabel = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
      <Fade in={true} timeout={800}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 5 }}>
            <IconButton 
              onClick={onBack} 
              sx={{ 
                mr: 2, 
                color: "primary.main",
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.1) }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1" fontWeight={800} sx={{ letterSpacing: -1 }}>
                Interview Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Detailed performance analysis and feedback.
              </Typography>
            </Box>
          </Box>
          
          {!isCompleted && (
            <Alert 
              severity="info" 
              icon={<HourglassTopIcon />} 
              sx={{ 
                mb: 4, 
                borderRadius: "16px",
                bgcolor: alpha(theme.palette.info.main, 0.1),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
              }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>Interview In Progress</AlertTitle>
              This is a preliminary report. Final scores will be generated once the interview is completed.
            </Alert>
          )}
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <SectionPaper title="Candidate Summary">
                <Stack alignItems="center" spacing={3} textAlign="center" pt={1}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                      width: 100,
                      height: 100,
                      fontSize: "3rem",
                      fontWeight: 800,
                      border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.1)}`
                    }}
                  >
                    {user?.charAt(0).toUpperCase() || "C"}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" component="h1" fontWeight={800}>
                      {user}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: "1rem", fontWeight: 400 }}>
                      {position_applied}
                    </Typography>
                  </Box>
                  <Chip
                    icon={<MilitaryTechIcon sx={{ color: "primary.main !important" }} />}
                    label={statusChipProps.label}
                    sx={{
                      bgcolor: alpha(theme.palette[statusChipProps.color].main, 0.1),
                      color: theme.palette[statusChipProps.color].main,
                      fontWeight: 800,
                      px: 2,
                      py: 3,
                      borderRadius: "15px",
                      fontSize: "1rem",
                      border: `1px solid ${alpha(theme.palette[statusChipProps.color].main, 0.2)}`
                    }}
                  />
                </Stack>

                <Divider sx={{ my: 3 }} />
                <Box sx={{ px: 1, textAlign: "left" }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <WorkHistoryIcon fontSize="small" />
                    Candidate Experience
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2.5, pl: 4 }}>
                    {candidate_experience || "Not available"}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <InterestsIcon fontSize="small" />
                    AI/ML Specializations
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, pl: 4 }}
                  >
                    {aiml_specialization && aiml_specialization.length > 0 ? (
                      aiml_specialization.map((spec) => (
                        <Chip
                          key={spec}
                          label={spec}
                          sx={{ 
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            color: "primary.main",
                            fontWeight: 600,
                            borderRadius: "10px",
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                          }}
                          size="small"
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        None specified
                      </Typography>
                    )}
                  </Box>

                </Box>
                <Divider sx={{ my: 3 }} />
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CalendarTodayIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Interview Date"
                      secondary={formatDateTime(interview_start_time)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {identity_verified ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <GppMaybeIcon color="warning" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="Identity Verified"
                      secondary={identity_verified ? "Yes" : "No"}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {malpractice_detected ? (
                        <GppBadIcon color="error" />
                      ) : (
                        <CheckCircleIcon color="success" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="Malpractice Detected"
                      secondary={malpractice_detected ? "Yes" : "No"}
                    />
                  </ListItem>
                </List>
              </SectionPaper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Stack spacing={4}>
                <SectionPaper title="Score Breakdown">
                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} sm={5} textAlign="center">
                      <OverallScoreProgress
                        value={global_readiness_score}
                        isCompleted={isCompleted}
                      />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                      <ScoreProgressBar
                        label="Language Proficiency"
                        score={language_proficiency_score}
                        isCompleted={isCompleted}
                      />
                      <ScoreProgressBar
                        label="Communication Skills"
                        score={communication_overall_score}
                        isCompleted={isCompleted}
                      />
                      <ScoreProgressBar
                        label="Psychometric Fit"
                        score={psychometric_overall_score}
                        isCompleted={isCompleted}
                      />
                      {isCompleted &&
                        technical_specialization_scores &&
                        Object.keys(technical_specialization_scores).length >
                          0 && (
                          <>
                            <Divider sx={{ my: 2 }}>
                              <Chip label="Technical" size="small" />
                            </Divider>
                            {Object.entries(
                              technical_specialization_scores
                            ).map(([key, score]) => (
                              <ScoreProgressBar
                                key={key}
                                label={formatTechnicalLabel(key)}
                                score={score}
                                isCompleted={isCompleted}
                              />
                            ))}
                          </>
                        )}
                    </Grid>
                  </Grid>
                </SectionPaper>
                <SectionPaper
                  title="Round-by-Round Analysis"
                  icon={<ModelTrainingIcon />}
                >
                  {roundDetails.length > 0 ? (
                    <Grid container spacing={3}>
                      {roundDetails.map((round) => (
                        <Grid item xs={12} sm={6} key={round.key}>
                          <Card
                            elevation={0}
                            sx={{ 
                              height: "100%", 
                              bgcolor: alpha(theme.palette.primary.main, 0.02),
                              borderRadius: "20px",
                              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&:hover": {
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                                borderColor: "primary.main",
                                transform: "translateY(-4px)"
                              }
                            }}
                          >
                            <CardHeader
                              avatar={
                                <Avatar
                                  sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: "primary.main",
                                    borderRadius: "12px",
                                    width: 48,
                                    height: 48
                                  }}
                                >
                                  {round.icon}
                                </Avatar>
                              }
                              title={
                                <Typography variant="h6" component="div" fontWeight={700} sx={{ fontSize: "1rem" }}>
                                  {round.title}
                                </Typography>
                              }
                              action={
                                <Chip
                                  label={`${round.data.overall_score}%`}
                                  sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: "primary.main",
                                    fontWeight: 800,
                                    borderRadius: "8px"
                                  }}
                                />
                              }
                            />
                            <CardContent sx={{ pt: 0 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ lineHeight: 1.6 }}
                              >
                                {round.data.round_summary}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>

                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 3,
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 2,
                        mt: 2,
                      }}
                    >
                      <InfoIcon color="primary" sx={{ mr: 2 }} />
                      <Typography color="text.secondary">
                        {isCompleted
                          ? "No round analysis data is available."
                          : "Round analysis will appear here once complete."}
                      </Typography>
                    </Box>
                  )}
                </SectionPaper>
                <SectionPaper
                  title="Analyst Notes on Language"
                  icon={<FormatQuoteIcon />}
                >
                  {language_analysis ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: "20px",
                        borderLeft: 6,
                        borderColor: "primary.main",
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                        position: "relative",
                        "&::before": {
                          content: '"\\201C"',
                          position: "absolute",
                          top: 10,
                          left: 15,
                          fontSize: "5rem",
                          color: alpha(theme.palette.primary.main, 0.1),
                          fontFamily: "serif",
                          lineHeight: 1
                        }
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontStyle: "italic", color: "text.primary", pl: 2, lineHeight: 1.8, position: "relative", zIndex: 1 }}
                      >
                        {language_analysis}
                      </Typography>
                    </Paper>

                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 3,
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 2,
                      }}
                    >
                      <InfoIcon color="primary" sx={{ mr: 2 }} />
                      <Typography color="text.secondary">
                        {isCompleted
                          ? "No language analysis was provided."
                          : "Language analysis will appear here once complete."}
                      </Typography>
                    </Box>
                  )}
                </SectionPaper>

                {/* --- NEW FULL TRANSCRIPT SECTION --- */}
                <SectionPaper
                  title="Full Interview Transcript"
                  icon={<QuestionAnswerIcon />}
                  sx={{ height: "auto" }}
                >
                  <Accordion
                    variant="outlined"
                    sx={{
                      bgcolor: "transparent",
                      border: "none",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="transcript-content"
                      id="transcript-header"
                      sx={{ p: 0 }}
                    >
                      <Typography>
                        Click to view all questions and answers
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pb: 0, pt: 2 }}>
                      {full_qa_transcript && full_qa_transcript.length > 0 ? (
                        <Stack spacing={3}>
                          {full_qa_transcript.map((qa, index) => (
                            <Paper
                              key={index}
                              elevation={0}
                              sx={{ 
                                p: 3, 
                                borderRadius: "20px",
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                bgcolor: alpha(theme.palette.primary.main, 0.01)
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                fontWeight={800}
                                color="primary.main"
                                gutterBottom
                                sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                              >
                                <Box sx={{ opacity: 0.5, fontSize: "0.8rem", mt: 0.5 }}>Q{index + 1}</Box>
                                {qa.question_text}
                              </Typography>
                              <Divider sx={{ my: 2, borderStyle: "dashed" }} />
                              <Stack spacing={1}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                                  Candidate Answer
                                </Typography>
                                <Typography
                                  variant="body1"
                                  color="text.primary"
                                  sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}
                                >
                                  {qa.answer || "No answer provided."}
                                </Typography>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>

                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 3,
                            border: "1px dashed",
                            borderColor: "divider",
                            borderRadius: 2,
                          }}
                        >
                          <InfoIcon color="primary" sx={{ mr: 2 }} />
                          <Typography color="text.secondary">
                            No transcript data is available for this interview.
                          </Typography>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </SectionPaper>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </Container>
  );
};

const AllReportsPage = () => {
  const { addNotification } = useNotification();
  const [reportsByPage, setReportsByPage] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalReports: 0,
  });

  const fetchPageOfReports = useCallback(async (pageNumber) => {
    const idListResponse = await apiClient.get(
      `/mock-interview/reports/?page=${pageNumber}`
    );
    const { ids, total_pages, total, page_size } = idListResponse.data;
    const pageInfo = {
      totalPages: total_pages,
      totalReports: total,
      pageSize: page_size,
      reports: [],
    };
    if (!ids || ids.length === 0) return pageInfo;
    const reportPromises = ids.map((id) =>
      apiClient.get(`/mock-interview/report/${id}/`)
    );
    const responses = await Promise.allSettled(reportPromises);
    pageInfo.reports = responses
      .filter((res) => res.status === "fulfilled" && res.value.data)
      .map((res) => res.value.data)
      .sort(
        (a, b) =>
          new Date(b.interview_start_time) - new Date(a.interview_start_time)
      );
    return pageInfo;
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);
      try {
        const page1Data = await fetchPageOfReports(1);
        setReportsByPage({ 1: page1Data.reports });
        setPagination({
          currentPage: 1,
          totalPages: page1Data.totalPages,
          totalReports: page1Data.totalReports,
        });
        if (addNotification)
          addNotification(`Loaded initial reports successfully`, "success");
        if (page1Data.totalPages > 1) {
          fetchPageOfReports(2)
            .then((page2Data) =>
              setReportsByPage((prev) => ({ ...prev, 2: page2Data.reports }))
            )
            .catch((err) => console.warn("Failed to pre-fetch page 2:", err));
        }
      } catch (err) {
        console.error("Failed to fetch initial reports:", err);
        const errorMessage =
          err.response?.data?.detail || "Could not load interview reports.";
        setError(errorMessage);
        if (addNotification) addNotification(errorMessage, "error");
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [fetchPageOfReports, addNotification]);

  const handlePageChange = useCallback(
    (event, newPage) => {
      setPagination((prev) => ({ ...prev, currentPage: newPage }));
      if (!reportsByPage[newPage]) {
        setReportsByPage((prev) => ({ ...prev, [newPage]: undefined }));
        fetchPageOfReports(newPage)
          .then((pageData) =>
            setReportsByPage((prev) => ({
              ...prev,
              [newPage]: pageData.reports,
            }))
          )
          .catch((err) => {
            const errorMessage =
              err.response?.data?.detail || `Could not load page ${newPage}.`;
            if (addNotification) addNotification(errorMessage, "error");
            setReportsByPage((prev) => ({ ...prev, [newPage]: [] }));
          });
      }
    },
    [reportsByPage, fetchPageOfReports, addNotification]
  );

  const handleSelectReport = useCallback((id) => setSelectedReportId(id), []);
  const handleReturnToList = useCallback(() => setSelectedReportId(null), []);
  const handleRefresh = useCallback(() => {
    const pageToRefresh = pagination.currentPage;
    setReportsByPage((prev) => ({ ...prev, [pageToRefresh]: undefined }));
    fetchPageOfReports(pageToRefresh)
      .then((pageData) => {
        setReportsByPage((prev) => ({
          ...prev,
          [pageToRefresh]: pageData.reports,
        }));
        setPagination((prev) => ({
          ...prev,
          totalPages: pageData.totalPages,
          totalReports: pageData.totalReports,
        }));
        if (addNotification)
          addNotification(`Refreshed page ${pageToRefresh}`, "success");
      })
      .catch((err) => {
        const errorMessage =
          err.response?.data?.detail ||
          `Could not refresh page ${pageToRefresh}.`;
        if (addNotification) addNotification(errorMessage, "error");
        setReportsByPage((prev) => ({ ...prev, [pageToRefresh]: [] }));
      });
  }, [pagination.currentPage, fetchPageOfReports, addNotification]);

  const selectedReport = useMemo(() => {
    if (!selectedReportId) return null;
    const allCachedReports = Object.values(reportsByPage)
      .flat()
      .filter(Boolean);
    return allCachedReports.find((report) => report.id === selectedReportId);
  }, [reportsByPage, selectedReportId]);

  const currentReports = reportsByPage[pagination.currentPage];
  const isListLoading = loading || currentReports === undefined;

  if (error && !isListLoading) {
    return (
      <Layout title="Error">
        <Container maxWidth="sm" sx={{ py: 4, textAlign: "center" }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
            <Alert severity="error">
              <AlertTitle>Error Loading Reports</AlertTitle>
              {error}
            </Alert>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              startIcon={<RefreshIcon />}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          </Paper>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout
      title={
        selectedReport
          ? `Report for ${selectedReport.user}`
          : "All Interview Reports"
      }
    >
      <CssBaseline />
      {selectedReportId ? (
        <InterviewReportDetail
          reportData={selectedReport}
          onBack={handleReturnToList}
        />
      ) : (
        <ReportsList
          reports={currentReports || []}
          onSelect={handleSelectReport}
          loading={isListLoading}
          onRefresh={handleRefresh}
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      )}
    </Layout>
  );
};

const InterviewReport = () => <AllReportsPage />;

export default InterviewReport;
